import {
  INSURANCE_COVERAGE_CHECK_COUNTRIES,
  INSURANCE_COVERAGE_CHECK_PLAN_TYPES,
  InsuranceCoverageCheckCountry,
  InsuranceCoverageCheckEntityKind,
  InsuranceCoverageCheckInsuranceType,
  InsuranceCoverageCheckPlanType,
  SaveInsuranceCoverageCheckData,
} from 'hydra-data-types';
import { isEmpty, isNonEmptyString, stripUndefined } from 'ts-utils';
import type { CoverageCheckLayoutConfig } from '../components/flows/InsuranceProviderScreen';
import type {
  CoverageConsent,
  CoverageConsentConfig,
  CoverageConsentsLayoutConfig,
  CoverageConsentTicks,
} from '../components/flows/layouts/CoverageConsentsLayout';
import {
  COVERAGE_PLAN_TYPE_EMPLOYER,
  COVERAGE_PLAN_TYPE_MEDICAID,
  COVERAGE_PLAN_TYPE_SELF_PURCHASED,
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  HEALTH_COVER_MEDICARE_ORIGINAL,
  NOT_LISTED_OPTION,
} from '../config/InsuranceProviders';
import { NervaControlScreenName } from '../config/nerva/NervaControlScreenNames';
import { StoreStateInternal } from '../store/Store';
import { UserDataState } from '../store/userData/UserDataState';
import { getCoverageAddress } from './CoverageAddress';
import { isAuCoverageAudience, isUsNon65Plus } from './MedicareAccess';
import { getPartnerInsurer, getPartnerInsurerAccessWithdrawal } from './PartnerInsurers';

const YES = 'Yes';
const NO = 'No';
const DATE_YMD = /^\d{4}-\d{2}-\d{2}$/;

/** The US or AU variant, whichever matches the user's coverage audience, if they're in one. */
function getCoverageAudienceVariant<Variant>(
  state: StoreStateInternal,
  us: Variant | undefined,
  au: Variant | undefined
): Variant | undefined {
  if (isUsNon65Plus(state)) return us;
  if (isAuCoverageAudience(state)) return au;
  return undefined;
}

/** The "check my coverage" variant for the user's coverage audience, if they're in one. */
export function getCoverageCheck(
  state: StoreStateInternal,
  layout: { coverageCheck?: CoverageCheckLayoutConfig; auCoverageCheck?: CoverageCheckLayoutConfig }
): CoverageCheckLayoutConfig | undefined {
  return getCoverageAudienceVariant(state, layout.coverageCheck, layout.auCoverageCheck);
}

/** The user gets their insurance through an employer, so named one we may ask. */
function isCoverageEmployerPath(userData: UserDataState): boolean {
  return userData.coveragePlanType?.[0] === COVERAGE_PLAN_TYPE_EMPLOYER;
}

type CoverageConsentsUserData = Pick<
  UserDataState,
  'coverageCheckAuthorization' | 'coverageAssignmentOfBenefits' | 'coverageEmployerOutreachConsent'
>;

/**
 * Where each coverage consent is saved, which is also the question it's tracked as: the keys the
 * provider and employer screens used before, so reports and segments keep working.
 */
export const COVERAGE_CONSENT_KEYS: Record<CoverageConsent, keyof CoverageConsentsUserData> = {
  healthPlan: 'coverageCheckAuthorization',
  assignmentOfBenefits: 'coverageAssignmentOfBenefits',
  employer: 'coverageEmployerOutreachConsent',
};

/** Every consent answered No, until the consents screen asks them again. */
const NO_COVERAGE_CONSENTS: CoverageConsentsUserData = Object.fromEntries(
  Object.values(COVERAGE_CONSENT_KEYS).map((key) => [key, [NO]])
);

/** The user asked us to contact their health plan (in AU, their private health insurer). */
export function hasHealthPlanConsent(userData: UserDataState): boolean {
  return userData.coverageCheckAuthorization?.[0] === YES;
}

/**
 * The consents the coverage consents screen offers, in order: the variant for the user's coverage
 * audience, with the employer consent only on the employer path so nobody is asked about an employer
 * they never named.
 */
export function getOfferedCoverageConsents(
  state: StoreStateInternal,
  layout: Pick<CoverageConsentsLayoutConfig, 'us' | 'au'>
): CoverageConsentConfig[] {
  const consents = getCoverageAudienceVariant(state, layout.us, layout.au) ?? [];
  return consents.filter(
    ({ consent }) => consent !== 'employer' || isCoverageEmployerPath(state.userData)
  );
}

/** The consents stored as Yes, so the screen starts with them ticked. */
export function getCoverageConsentTicks(userData: UserDataState): CoverageConsentTicks {
  return Object.fromEntries(
    Object.entries(COVERAGE_CONSENT_KEYS).map(([consent, key]) => [
      consent,
      userData[key]?.[0] === YES,
    ])
  );
}

/** What the coverage consents screen saves: Yes or No for each consent it offered. */
export function getCoverageConsentsUserData(
  offered: readonly CoverageConsent[],
  ticks: CoverageConsentTicks
): CoverageConsentsUserData {
  return Object.fromEntries(
    offered.map((consent) => [COVERAGE_CONSENT_KEYS[consent], [ticks[consent] === true ? YES : NO]])
  );
}

/**
 * What the user did on the insurance-provider screen: the primary button, the self-pay link, or the
 * AU "I don't have private health insurance" link.
 */
export type InsuranceProviderSubmission =
  | { action: 'submit'; provider: string }
  | { action: 'selfPay'; provider?: string }
  | { action: 'noPrivateInsurance' };

/**
 * A partner insurer picked where the coverage check was offered: its no-cost access offer replaces
 * the check.
 */
function isPartnerInsurerPick(
  submission: InsuranceProviderSubmission,
  coverageCheck: CoverageCheckLayoutConfig | undefined
): boolean {
  return (
    submission.action === 'submit' &&
    coverageCheck !== undefined &&
    getPartnerInsurer(submission.provider) !== undefined
  );
}

/**
 * What the insurance-provider screen saves. The coverage keys are read back by
 * `getCoverageEntryRoute`, so they decide whether the user goes on into the coverage screens. The
 * consents are asked later, on the coverage consents screen; a submit answers them No until then, so
 * consents from an earlier visit can't file the check before the user decides again. The exits
 * withdraw any health plan authorization given earlier so a user who went back to take one isn't
 * filed for outreach, and every exit offered but not taken is answered No. A partner insurer
 * replaces the coverage check with its own offer, so it records only the exits, and the offer
 * records the choice; any other answer withdraws partner access taken earlier.
 */
export function getInsuranceProviderUserData(
  screenName: string,
  submission: InsuranceProviderSubmission,
  coverageCheck: CoverageCheckLayoutConfig | undefined,
  storedUserData: UserDataState
): UserDataState {
  const answers = getProviderScreenAnswers(screenName, submission, coverageCheck);
  return isPartnerInsurerPick(submission, coverageCheck)
    ? answers
    : { ...answers, ...getPartnerInsurerAccessWithdrawal(storedUserData) };
}

function getProviderScreenAnswers(
  screenName: string,
  submission: InsuranceProviderSubmission,
  coverageCheck: CoverageCheckLayoutConfig | undefined
): UserDataState {
  const noPrivateInsuranceNo =
    coverageCheck?.noPrivateInsuranceTitle === undefined
      ? {}
      : { coverageNoPrivateInsurance: [NO] };
  if (submission.action === 'noPrivateInsurance') {
    return {
      coverageNoPrivateInsurance: [YES],
      coverageSelfPay: [NO],
      coverageCheckAuthorization: [NO],
    };
  }
  if (submission.action === 'selfPay') {
    const { provider } = submission;
    return {
      coverageSelfPay: [YES],
      coverageCheckAuthorization: [NO],
      ...noPrivateInsuranceNo,
      ...(provider === undefined || isEmpty(provider) ? {} : { [screenName]: [provider] }),
    };
  }
  const { provider } = submission;
  if (coverageCheck === undefined) return { [screenName]: [provider] };
  if (isPartnerInsurerPick(submission, coverageCheck)) {
    return { [screenName]: [provider], coverageSelfPay: [NO], ...noPrivateInsuranceNo };
  }
  return {
    [screenName]: [provider],
    coverageSelfPay: [NO],
    ...noPrivateInsuranceNo,
    ...NO_COVERAGE_CONSENTS,
  };
}

/** The stored date of birth when it is in the `YYYY-MM-DD` form the callable accepts. */
function getDateOfBirth(userData: UserDataState): string | undefined {
  const dob = userData.dateOfBirth?.[0];
  return dob !== undefined && DATE_YMD.test(dob) ? dob : undefined;
}

/** The self-pay exit or (AU) "I don't have private health insurance", chosen instead of the check. */
function hasTakenCoverageExit(userData: UserDataState): boolean {
  return userData.coverageSelfPay?.[0] === YES || userData.coverageNoPrivateInsurance?.[0] === YES;
}

/**
 * The user asked us to contact someone: their health plan, or (on the employer path) their employer.
 * An employer consent stored before the user changed their plan type doesn't count.
 */
function hasRequestedOutreach(userData: UserDataState): boolean {
  return (
    hasHealthPlanConsent(userData) ||
    (isCoverageEmployerPath(userData) && userData.coverageEmployerOutreachConsent?.[0] === YES)
  );
}

/**
 * The coverage check is complete once the user asked us to contact their health plan or employer
 * (on the consents screen, the last before the result), answered the address step (chose an address
 * or said they couldn't find it), and has a date of birth on file. Both flows ask date of birth
 * before the coverage screens, and the callable requires it alongside advocacy consent, so without
 * it the save would only be rejected. AU has no address step; its consents come after the optional
 * membership number, so it's filed once, with the number. Taking an exit rules it out even if an
 * earlier consent is still stored, e.g. a user who consented, went back and chose to self-pay.
 * Picking a partner insurer rules it out the same way, since those members are offered no-cost
 * access instead. A check already filed by then stays filed.
 */
export function isCoverageCheckComplete(state: StoreStateInternal): boolean {
  const { userData } = state;
  return (
    hasRequestedOutreach(userData) &&
    !hasTakenCoverageExit(userData) &&
    getPartnerInsurer(getProviderAnswer(userData)) === undefined &&
    (isAuCoverageAudience(state) ||
      getCoverageAddress(userData) !== undefined ||
      userData.coverageAddressNotFound !== undefined) &&
    getDateOfBirth(userData) !== undefined
  );
}

export function getProviderAnswer(userData: UserDataState): string | undefined {
  const answers = userData as Partial<Record<string, string[]>>;
  return answers[NervaControlScreenName.InsuranceProvider]?.[0];
}

function isPlanType(value: string | undefined): value is InsuranceCoverageCheckPlanType {
  return INSURANCE_COVERAGE_CHECK_PLAN_TYPES.includes(value as InsuranceCoverageCheckPlanType);
}

/** An Original Medicare dropdown pick skips the plan-type screen, so it stands in for the answer. */
function getPlanType(
  userData: UserDataState,
  provider: string | undefined
): InsuranceCoverageCheckPlanType {
  const answered = userData.coveragePlanType?.[0];
  if (isPlanType(answered)) return answered;
  return provider === HEALTH_COVER_MEDICARE_ORIGINAL ? HEALTH_COVER_MEDICARE_ORIGINAL : '';
}

const PUBLIC_PROGRAMS: string[] = [
  HEALTH_COVER_MEDICARE_ORIGINAL,
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  COVERAGE_PLAN_TYPE_MEDICAID,
];

function getInsuranceType(
  planType: InsuranceCoverageCheckPlanType
): InsuranceCoverageCheckInsuranceType {
  if (planType === COVERAGE_PLAN_TYPE_EMPLOYER) return 'Employer-sponsored insurance';
  if (planType === COVERAGE_PLAN_TYPE_SELF_PURCHASED) return 'Private health insurance';
  if (PUBLIC_PROGRAMS.includes(planType)) return 'Public / government program';
  return '';
}

/** The payer entity the answers point at; the employer is the higher-value key for US advocacy. */
function getEntityKind(
  planType: InsuranceCoverageCheckPlanType,
  insurer: string
): InsuranceCoverageCheckEntityKind {
  if (planType === COVERAGE_PLAN_TYPE_EMPLOYER) return 'employer';
  if (PUBLIC_PROGRAMS.includes(planType)) return 'program';
  return insurer === '' ? '' : 'insurer';
}

function getCountry(country: string): InsuranceCoverageCheckCountry {
  return INSURANCE_COVERAGE_CHECK_COUNTRIES.includes(country as InsuranceCoverageCheckCountry)
    ? (country as InsuranceCoverageCheckCountry)
    : 'OTHER';
}

function consentGiven(answer: string[] | undefined): boolean | undefined {
  return answer === undefined ? undefined : answer[0] === YES;
}

type PolicyLookup = Pick<
  SaveInsuranceCoverageCheckData,
  'insurer' | 'dob' | 'memberId' | 'address'
>;

/**
 * The details for finding the user's policy with their insurer, which only the health plan
 * authorization covers. The employer consent alone covers their name, employer and request.
 */
function getPolicyLookup(userData: UserDataState, provider: string | undefined): PolicyLookup {
  if (!hasHealthPlanConsent(userData)) return { insurer: '', dob: '', memberId: '' };
  return {
    // "Not listed" carries no payer.
    insurer: provider === undefined || provider === NOT_LISTED_OPTION ? '' : provider,
    dob: getDateOfBirth(userData) ?? '',
    memberId: userData.coverageMemberId?.[0] ?? '',
    address: getCoverageAddress(userData),
  };
}

/**
 * Maps the coverage-flow answers and account details onto the `/insurance_coverage_check` document
 * the website form also writes. Returns `undefined` when the flow isn't complete or there is no
 * account email to file it under. `id` is set once a document exists so the save updates it.
 * `sourcePath` is the flow config name (e.g. NervaControl), passed in so this module doesn't import
 * the flow configs.
 */
export function buildInsuranceCoverageCheck(
  state: StoreStateInternal,
  sourcePath: string
): SaveInsuranceCoverageCheckData | undefined {
  const { user, userData, entry } = state;
  if (!isCoverageCheckComplete(state) || !isNonEmptyString(user.email)) return undefined;

  const provider = getProviderAnswer(userData);
  const policyLookup = getPolicyLookup(userData, provider);
  const planType = getPlanType(userData, provider);
  // Only email accounts carry a last name; token users have just `name`.
  const lastName = 'lastName' in user ? user.lastName : undefined;

  const check: SaveInsuranceCoverageCheckData = {
    id: userData.insuranceCoverageCheckId?.[0],
    source: 'webAssessment',
    sourcePath,
    country: getCountry(entry.country),
    ...policyLookup,
    employer: userData.coverageUsEmployer?.[0] ?? '',
    entityKind: getEntityKind(planType, policyLookup.insurer),
    // A curated dropdown pick counts as verified.
    entityVerified: policyLookup.insurer !== '',
    name: [user.name, lastName].filter(isNonEmptyString).join(' '),
    planType,
    // AU only asks for a private health insurer, and has no plan-type question.
    insuranceType: isAuCoverageAudience(state)
      ? 'Private health insurance'
      : getInsuranceType(planType),
    coverDetail: (userData.coverageBenefits ?? []).join(', '),
    email: user.email,
    // Only users who asked us to contact their plan or employer get this far, so we may follow up.
    // Naming them to their insurer needs the health plan authorization; the website names them to
    // their employer on employerOutreachConsent.
    consent: true,
    advocacyConsent: hasHealthPlanConsent(userData),
    healthPlanConsent: consentGiven(userData.coverageCheckAuthorization),
    // AU isn't asked it; a provider submit stores No for every consent until the consents screen.
    assignmentOfBenefitsConsent: isAuCoverageAudience(state)
      ? undefined
      : consentGiven(userData.coverageAssignmentOfBenefits),
    employerOutreachConsent: isCoverageEmployerPath(userData)
      ? consentGiven(userData.coverageEmployerOutreachConsent)
      : undefined,
  };
  // The callable's schema rejects unknown keys; undefined optionals must not be sent as keys.
  return stripUndefined(check);
}
